import { z } from "zod";

/**
 * The availability light (top corner): how soon online booking offers clinic visits right now.
 * green: no delay; yellow: a delay (the normal one from settings, or one for this time only);
 * red: nothing online until the next nurse shift starts or ends.
 */
export type LightColor = "green" | "yellow" | "red";

export interface AvailabilityLightView {
  color: LightColor;
  /** Yellow: minutes of delay in effect. */
  delayMinutes: number;
  /** Its normal state (from settings): yellow when the booking delay is more than 0, else green. */
  normal: LightColor;
  normalDelayMinutes: number;
  /** A change from normal: who or what made it, when, and until when (the next shift start or end). */
  change: { by: string; at: string; until: string | null } | null;
  /** Red because no nurse is on shift: when the next shift starts (null: none this week). */
  noNurseUntil?: string | null;
  /** Red because every nurse working now is at an event: the events, and when the first of them ends. */
  atEvent?: { names: string[]; until: string };
  /** No nurse on shift, but someone (or a door link) set this color: it shows as red, since nothing can be booked anyway. */
  held?: LightColor;
  /** This person can change it (a nurse on shift, managers, admins). */
  canChange: boolean;
}

/** Set the light: a color, or back to normal. Yellow can carry a delay for this time only. */
export const availabilityLightSchema = z.object({
  color: z.enum(["green", "yellow", "red", "normal"]),
  minutes: z.number().int().min(0).max(1440).nullable().default(null),
});
export type AvailabilityLightInput = z.infer<typeof availabilityLightSchema>;

export interface AvailabilityHookView {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string | null;
  /** For a door's alarm: which address means the door opened, and which that it closed (null: not a door). */
  doorOpenColor: DoorColor | null;
  doorClosedColor: DoorColor | null;
}

export type DoorColor = "green" | "yellow" | "red" | "normal";

/** A new link: the address for each color (the secret is only ever shown now). */
export interface AvailabilityHookCreated extends AvailabilityHookView {
  urls: { green: string; yellow: string; red: string; normal: string };
}
